/**
 * WarrantyExpiringReport (REPORT-WARRANTY) — equipment whose warranty expires in
 * the next N days, with the customer's phone/email. The proactive-outreach list
 * (ServiceTitan's highest-ROI report family): catch a unit before its warranty
 * lapses. Reads the ESC equipment + location mirror (read-only).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download, ExternalLink } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { classifyEquipment, CATEGORY_LABEL, type EquipmentRow } from "@/lib/equipment";
import { cn } from "@/lib/cn";
import { downloadCsv } from "./csv";

const WINDOWS = [30, 60, 90, 180];
const iso = (d: Date) => d.toISOString().slice(0, 10);

interface Row extends EquipmentRow {
  custno: string; locno: string | null;
  customer?: string | null; address?: string | null; phone?: string | null; email?: string | null;
}

export function WarrantyExpiringReport() {
  const [days, setDays] = useState(90);
  const today = iso(new Date());
  const horizon = iso(new Date(Date.now() + days * 86400000));

  const { data, isFetching } = useQuery({
    queryKey: ["warranty_expiring", days],
    queryFn: async (): Promise<Row[]> => {
      const { data: eq } = await supabase
        .from("mirror_equipment")
        .select("custno, locno, counter, mfg, model, serial, eq_type, equip_loc, install_date, warranty_date, ser_agr_no, notes, custom_1, custom_2, invoice")
        .gte("warranty_date", today).lte("warranty_date", horizon)
        .order("warranty_date").limit(500);
      const rows = (eq ?? []) as Row[];
      if (rows.length === 0) return [];
      const custnos = [...new Set(rows.map((r) => r.custno))];
      const [{ data: custs }, { data: locs }] = await Promise.all([
        supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos),
        supabase.from("mirror_location").select("custno, locno, add1, city, phone1, email").in("custno", custnos),
      ]);
      const cMap = new Map((custs ?? []).map((c: { custno: string; fullname: string }) => [c.custno, c.fullname]));
      const lMap = new Map((locs ?? []).map((l: Record<string, string>) => [`${l.custno}|${l.locno}`, l]));
      return rows.map((r) => {
        const l = lMap.get(`${r.custno}|${r.locno}`);
        return {
          ...r, customer: cMap.get(r.custno) ?? r.custno,
          address: l ? `${l.add1 ?? ""}${l.city ? ", " + l.city : ""}` : null,
          phone: l?.phone1 ?? null, email: l?.email ?? null,
        };
      });
    },
  });

  const rows = useMemo(() => (data ?? []).map((r) => ({
    ...r, category: classifyEquipment(r).category,
    daysLeft: Math.round((new Date(r.warranty_date! + "T00:00:00").getTime() - Date.now()) / 86400000),
  })), [data]);

  const exportCsv = () => downloadCsv(`warranty-expiring-${days}d`, [
    ["Warranty ends", "Days left", "Category", "Model", "Customer", "Address", "Phone", "Email", "Customer #"],
    ...rows.map((r) => [r.warranty_date ?? "", r.daysLeft, CATEGORY_LABEL[r.category], r.model ?? "", r.customer ?? "", r.address ?? "", r.phone ?? "", r.email ?? "", r.custno]),
  ]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-500">Expiring within</span>
        <div className="inline-flex overflow-hidden rounded-md border border-hairline">
          {WINDOWS.map((w) => (
            <button key={w} type="button" onClick={() => setDays(w)}
              className={cn("px-3 py-1.5 text-sm", days === w ? "bg-brand-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50")}>
              {w}d
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-400">{rows.length} units</span>
        {rows.length > 0 && (
          <button type="button" onClick={exportCsv}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
            <Download className="h-4 w-4" /> CSV
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border border-hairline bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hairline bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <th className="px-3 py-2">Ends</th><th className="px-3 py-2">Left</th><th className="px-3 py-2">Equipment</th>
              <th className="px-3 py-2">Customer</th><th className="px-3 py-2">Contact</th><th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {isFetching && <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-400">Loading…</td></tr>}
            {!isFetching && rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-400">No warranties expiring in this window.</td></tr>}
            {rows.map((r) => (
              <tr key={r.counter} className="border-b border-hairline last:border-0 hover:bg-slate-50">
                <td className="whitespace-nowrap px-3 py-2 text-slate-600">{r.warranty_date}</td>
                <td className="px-3 py-2"><span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", r.daysLeft <= 30 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600")}>{r.daysLeft}d</span></td>
                <td className="px-3 py-2 text-slate-700">{CATEGORY_LABEL[r.category]} <span className="text-xs text-slate-400">{r.model}</span></td>
                <td className="px-3 py-2 text-slate-700">{r.customer}<div className="text-xs text-slate-400">{r.address}</div></td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.phone}{r.email ? <div className="text-slate-400">{r.email}</div> : null}</td>
                <td className="px-3 py-2"><Link to={`/customers/${r.custno}?loc=${r.locno ?? ""}&tab=equipment`} className="text-slate-300 hover:text-brand-600"><ExternalLink className="h-4 w-4" /></Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
